import useUpdateParkingServiceMetadata from '../../../hooks/parking-services/useUpdateParkingServiceMetadata.ts';
import useParkingService from '../../../hooks/parking-services/useParkingService.ts';
import MutateParkingServiceHeaderInfoForm, {
  MutateParkingServiceHeaderInfoFormValues,
} from '../../ui/places/MutateParkingServiceHeaderInfoForm.tsx';
import {
  UpdateParkingServiceMetadataRequest,
  UpdateParkingServiceTagsRequest,
} from '../../../api/requests/parking-service-request.ts';
import useEnableParkingService from '../../../hooks/parking-services/useEnableParkingService.ts';
import usePauseParkingService from '../../../hooks/parking-services/usePauseParkingService.ts';
import useDisableParkingService from '../../../hooks/parking-services/useDisableParkingService.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import useUpdateParkingServiceTags from '../../../hooks/parking-services/useUpdateParkingServiceTags.ts';
import { LoadingOverlay, Modal } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import type { ErrorResponse } from '../../../api/responses/error-response.ts';

interface UpdateParkingServiceHeaderInfoModalProps {
  opened: boolean;
  onClose: () => void;
  serviceId: string;
  onSuccess?: () => void;
}

const UpdateParkingServiceHeaderInfoModal = ({
  opened,
  onClose,
  serviceId,
  onSuccess,
}: UpdateParkingServiceHeaderInfoModalProps) => {
  const { data: parkingService, isLoading: isParkingServiceLoading } =
    useParkingService(serviceId);

  const { mutateAsync: updateParkingServiceMetadata } =
    useUpdateParkingServiceMetadata();
  const { mutateAsync: updateParkingServiceTags } =
    useUpdateParkingServiceTags();
  const { mutateAsync: enableParkingService } = useEnableParkingService();
  const { mutateAsync: pauseParkingService } = usePauseParkingService();
  const { mutateAsync: disableParkingService } = useDisableParkingService();

  const {
    mutate: handleSubmit,
    isPending: isUpdateTagsMetadataAndStatusPending,
  } = useMutation<
    void,
    ErrorResponse,
    MutateParkingServiceHeaderInfoFormValues
  >({
    mutationFn: async (formValues) => {
      const updateParkingServiceMetadataRequest: UpdateParkingServiceMetadataRequest =
        {
          brandId: parkingService.brandId ?? '',
          buildingTypes: parkingService.buildingTypes,
          floorLevels: parkingService.floorLevels,
          hasElevator: parkingService.hasElevator,
          hasParkingBooth: parkingService.hasParkingBooth,
          hasRestroom: parkingService.hasRestroom,
          heightLimit: parkingService.heightLimit,
          mechanicalSpaces: parkingService.mechanicalSpaces,
          notes: parkingService.notes,
          parkingLotTypes: parkingService.parkingLotTypes,
          phoneNumber: parkingService.phoneNumber,
          pricingSource: parkingService.pricingSource,
          serviceId: serviceId,
          website: parkingService.website,
          name: formValues.name,
        };

      await updateParkingServiceMetadata(updateParkingServiceMetadataRequest);

      const updateParkingServiceTagsRequest: UpdateParkingServiceTagsRequest = {
        serviceId: serviceId,
        tags: formValues.tags,
      };

      await updateParkingServiceTags(updateParkingServiceTagsRequest);

      if (formValues.serviceStatus === 'ONLINE') {
        await enableParkingService(serviceId);
      } else if (formValues.serviceStatus === 'TEMPORARY_OFFLINE') {
        await pauseParkingService(serviceId);
      } else {
        await disableParkingService(serviceId);
      }
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      notifyError('更新服務資訊失敗', error.message);
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="編輯停車資訊"
      centered
      closeOnClickOutside={false}
      size={440}
      styles={{
        title: { fontWeight: 700, fontSize: 16 },
        header: { paddingBottom: 24 },
      }}
    >
      {isParkingServiceLoading ? (
        <LoadingOverlay visible />
      ) : (
        <MutateParkingServiceHeaderInfoForm
          initialValues={{
            name: parkingService.name,
            serviceStatus: parkingService.serviceStatus,
            tags: parkingService.tags,
          }}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isSubmitting={isUpdateTagsMetadataAndStatusPending}
        />
      )}
    </Modal>
  );
};

export { UpdateParkingServiceHeaderInfoModal };
